/**
 * 健康度診断カード（エンジニア看板）。
 *
 * 戦闘力サマリーの下に、選択中スコープ（ACE / PDG TE / ACE 各チーム）のメンバーごとに
 * 「担当中PJの平均健康度」を表形式で一覧する。戦闘力サマリーと同じく team 別（代表色見出し）
 * に分割し、team の平均健康度と、全体PJ看板の対応チームビューへの導線を出す。個々の担当
 * 案件は出さず、健康度の rank + 数値のみを表示する。
 *
 * - ACE ビュー: airCloset / Platform / Mall（QA は健康度診断対象外）。
 * - PDG TE ビュー: PDG TE 全体 + カスタムビュー 3 サブチーム別。
 * - ACE 各チームビュー: そのチーム単体。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エンジニア看板のスコープ別に、メンバー担当PJの平均健康度診断を team 別テーブル（代表色見出し）で表示し全体PJ看板へ導線する
 * @graph-connects useProjects [calls] プロジェクト一覧（健康度 + アサイン）取得フック呼び出し
 * @graph-connects useCombatPowerCustomViews [calls] PDG TE カスタムビュー（サブチーム定義）取得フック呼び出し
 * @graph-connects calcAvgHealth [calls] メンバーの平均健康度算出
 * @graph-connects calcTeamAvgHealth [calls] チームの担当中PJ（案件単位で重複排除）の平均健康度算出
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useProjects } from '../../../hooks/index.js';
import { useCombatPowerCustomViews } from '../../../hooks/useCombatPower.js';
import { HEALTH_COLORS } from './HealthBadge.js';
import { calcAvgHealth, calcTeamAvgHealth, type AvgHealth } from './memberHealth.js';
import { ACE_SUB_TEAMS } from '../combat-power/AceTeamsChart.js';
import type { CombatPowerCustomView } from '../../../types/combat-power.js';
import type { Member, MemberOrganization } from '../../../types/api.js';
import type { Project } from '../../../types/project.js';

/**
 * ACE サブチーム key → 全体PJ看板のチームタブ key（TEAM_OPTIONS の key）。
 * @graph-connects none
 */
const ACE_TEAM_TO_PROJECT_KEY: Record<(typeof ACE_SUB_TEAMS)[number]['key'], string | null> = {
  aircloset: 'ace-ac',
  platform: 'ace-platform',
  mall: 'ace-mall',
  qa: null,
};

/**
 * team 見出しの代表色（戦闘力サマリー AceTeamTables と同一）。
 * @graph-connects none
 */
const TEAM_ACCENT: Record<string, string> = {
  aircloset: '#ef4444',
  platform: '#3b82f6',
  mall: '#22c55e',
  qa: '#eab308',
};

/** PDG TE 全体ブロックの代表色。 @graph-connects none */
const PDG_TE_ACCENT = '#6366f1';

/** PDG TE カスタムビュー 3 サブチームの代表色。 @graph-connects none */
const CUSTOM_VIEW_ACCENTS = ['#8b5cf6', '#06b6d4', '#f59e0b'];

/** 健康度診断テーブルの 1 行（メンバー）。 */
interface HealthMemberRow {
  /** React key（email もしくはメンバー名）。 */
  key: string;
  /** 表示名。 */
  displayName: string;
  /** 担当PJ健康度の引き当てキー。解決できない場合 null（health は "-"）。 */
  email: string | null;
}

interface HealthTeamBlock {
  key: string;
  label: string;
  /** 見出しの代表色（未指定はデフォルト文字色）。 */
  accentColor?: string;
  /** 全体PJ看板の対応チームタブ key。null は対応ビューなし（詳細導線を出さない）。 */
  projectTeamKey: string | null;
  members: HealthMemberRow[];
}

interface MemberHealthDiagnosisSectionProps {
  /** 選択中の組織フィルタ（undefined = 全体）。 */
  selectedOrg: MemberOrganization | undefined;
  /** 選択スコープのエンジニアメンバー（useMemberList 由来）。 */
  engineers: Member[];
  /** 選択スコープの QA メンバー（useMemberList 由来）。 */
  qaMembers: Member[];
}

/** Member → 健康度診断行。 @graph-connects none */
function memberToRow(m: Member): HealthMemberRow {
  return { key: m.email, displayName: m.nickName ?? m.name, email: m.email };
}

/** 選択スコープを team 別ブロックへ分解する。 @graph-connects none */
export function buildTeamBlocks(
  selectedOrg: MemberOrganization | undefined,
  allMembers: Member[],
  customViews: CombatPowerCustomView[],
  nameToEmail: Map<string, string>,
): HealthTeamBlock[] {
  switch (selectedOrg) {
    case 'ace':
      // QA は健康度診断の対象外。見出しは戦闘力サマリーと同じ短縮ラベル + 代表色。
      return ACE_SUB_TEAMS.filter((st) => st.key !== 'qa').map((st) => ({
        key: st.key,
        label: st.label,
        accentColor: TEAM_ACCENT[st.key],
        projectTeamKey: ACE_TEAM_TO_PROJECT_KEY[st.key],
        members: allMembers.filter((m) => m.teamName === st.team).map(memberToRow),
      }));
    case 'tet': {
      // PDG TE 全体 + カスタムビュー（サブチーム）別。カスタムビューは名前で担当PJ健康度を引き当てる。
      const base: HealthTeamBlock = {
        key: 'pdg-te',
        label: 'PDG TE',
        accentColor: PDG_TE_ACCENT,
        projectTeamKey: 'pdg',
        members: allMembers.map(memberToRow),
      };
      const customBlocks: HealthTeamBlock[] = customViews
        .filter((v) => v.memberNames.length > 0)
        .map((v, i) => ({
          key: v.id,
          label: v.name,
          accentColor: CUSTOM_VIEW_ACCENTS[i % CUSTOM_VIEW_ACCENTS.length],
          // サブチーム専用の全体PJ看板ビューは無いため詳細導線は出さない
          projectTeamKey: null,
          members: v.memberNames.map((name) => ({
            key: name,
            displayName: name,
            email: nameToEmail.get(name.toLowerCase()) ?? null,
          })),
        }));
      return [base, ...customBlocks];
    }
    case 'aircloset':
      return [
        {
          key: 'aircloset',
          label: 'ACE airCloset',
          accentColor: TEAM_ACCENT.aircloset,
          projectTeamKey: 'ace-ac',
          members: allMembers.map(memberToRow),
        },
      ];
    case 'platform':
      return [
        {
          key: 'platform',
          label: 'ACE Platform',
          accentColor: TEAM_ACCENT.platform,
          projectTeamKey: 'ace-platform',
          members: allMembers.map(memberToRow),
        },
      ];
    case 'mall':
      return [
        {
          key: 'mall',
          label: 'ACE Mall',
          accentColor: TEAM_ACCENT.mall,
          projectTeamKey: 'ace-mall',
          members: allMembers.map(memberToRow),
        },
      ];
    case 'qa':
      return [
        {
          key: 'qa',
          label: 'ACE QA',
          accentColor: TEAM_ACCENT.qa,
          projectTeamKey: null,
          members: allMembers.map(memberToRow),
        },
      ];
    default:
      return [];
  }
}

/** 平均健康度バッジ（グレード + 平均点のみ。ラベル文字は tooltip に格納）。null は "-"。 @graph-connects none */
function HealthAvgBadge({ avg }: { avg: AvgHealth | null }) {
  const { t } = useTranslation();
  if (!avg) return <span className="text-xs text-[var(--color-text-muted)]">-</span>;
  const cfg = HEALTH_COLORS[avg.grade];
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.text }}
      title={`${t('members.healthDiagnosis.avgHealth')}: ${t(cfg.labelKey)} (${String(avg.score)}${t('projects.health.unit')})`}
    >
      {avg.grade}
      <span className="font-normal">
        {String(avg.score)}
        {t('projects.health.unit')}
      </span>
    </span>
  );
}

/** 行の担当中PJを引く（email 未解決は空配列）。 @graph-connects none */
function rowProjects(email: string | null, projectsByEmail: Map<string, Project[]>): Project[] {
  if (!email) return [];
  return projectsByEmail.get(email.toLowerCase()) ?? [];
}

/** 1 チーム分の健康度診断テーブル。 @graph-connects none */
function TeamHealthTable({
  block,
  projectsByEmail,
}: {
  block: HealthTeamBlock;
  projectsByEmail: Map<string, Project[]>;
}) {
  const { t } = useTranslation();

  // チームの担当中PJ（案件単位で重複排除）から team 平均健康度を算出
  const teamAvg = useMemo(
    () => calcTeamAvgHealth(block.members.map((m) => rowProjects(m.email, projectsByEmail))),
    [block.members, projectsByEmail],
  );

  // メンバーは表示名昇順
  const sortedMembers = useMemo(
    () => [...block.members].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [block.members],
  );

  return (
    <div className="min-w-0">
      <div
        className="mb-1.5 rounded px-2 py-1"
        style={
          block.accentColor
            ? {
                backgroundColor: `${block.accentColor}1A`,
                borderLeft: `3px solid ${block.accentColor}`,
              }
            : undefined
        }
      >
        <h4
          className="text-sm font-bold flex items-baseline gap-1 min-w-0"
          style={block.accentColor ? { color: block.accentColor } : undefined}
        >
          <span className="truncate">{block.label}</span>
          <span className="font-normal opacity-70 shrink-0">({String(sortedMembers.length)})</span>
        </h4>
      </div>
      <div className="flex items-center justify-between gap-1 mb-2">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-[var(--color-text-muted)] shrink-0">
            {t('members.healthDiagnosis.teamAvg')}
          </span>
          <HealthAvgBadge avg={teamAvg} />
        </span>
        {block.projectTeamKey && (
          <Link
            to="/projects/all-pj-board"
            search={{ team: block.projectTeamKey }}
            className="inline-flex items-center gap-0.5 text-xs font-medium text-[var(--color-accent-primary)] hover:underline shrink-0"
            aria-label={t('actions.viewDetails')}
            title={t('actions.viewDetails')}
          >
            {t('members.healthDiagnosis.detail')}
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>
      {sortedMembers.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] py-3">{t('members.noMembers')}</p>
      ) : (
        <table className="w-full table-fixed">
          <colgroup>
            <col />
            <col className="w-[84px]" />
          </colgroup>
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left py-1 px-2 text-xs font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {t('members.healthDiagnosis.member')}
              </th>
              <th className="text-left py-1 px-2 text-xs font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {t('members.healthDiagnosis.avgHealth')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.map((m) => (
              <tr key={m.key} className="border-b border-[var(--color-border)] last:border-b-0">
                <td className="py-1 px-2 text-xs text-[var(--color-text-primary)] truncate">
                  {m.displayName}
                </td>
                <td className="py-1 px-2">
                  <HealthAvgBadge avg={calcAvgHealth(rowProjects(m.email, projectsByEmail))} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** @graph-connects none */
export function MemberHealthDiagnosisSection({
  selectedOrg,
  engineers,
  qaMembers,
}: MemberHealthDiagnosisSectionProps) {
  const { t } = useTranslation();
  const { data: projectData } = useProjects();
  // カスタムビュー（PDG TE のサブチーム定義）は PDG TE ビューのみ取得する
  const { data: cvData } = useCombatPowerCustomViews(selectedOrg === 'tet');
  const customViews = useMemo(() => cvData?.views ?? [], [cvData]);

  // email(小文字) → 担当中PJ（完了除外、メンバー単位で案件重複排除）
  const projectsByEmail = useMemo(() => {
    const map = new Map<string, Project[]>();
    if (!projectData) return map;
    const allProjects = [
      ...projectData.integratedProducts.flatMap((ip) => ip.chunkProjects),
      ...projectData.standaloneProjects,
    ];
    const byId = new Map(allProjects.map((p) => [p.id, p]));
    const seenByEmail = new Map<string, Set<number>>();
    for (const a of projectData.memberAssignments) {
      const p = byId.get(a.projectId);
      if (!p || p.status === '完了') continue;
      const email = a.memberEmail.toLowerCase();
      let seen = seenByEmail.get(email);
      if (!seen) {
        seen = new Set();
        seenByEmail.set(email, seen);
      }
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      const list = map.get(email) ?? [];
      list.push(p);
      map.set(email, list);
    }
    return map;
  }, [projectData]);

  // メンバー名(小文字) → email。カスタムビュー（名前で定義）の担当PJ引き当て用。
  // アサインを持つメンバーのみ引ければ十分（担当PJ0件は health も "-" になるため）。
  const nameToEmail = useMemo(() => {
    const map = new Map<string, string>();
    if (!projectData) return map;
    for (const a of projectData.memberAssignments) {
      const key = a.memberName.toLowerCase();
      if (!map.has(key)) map.set(key, a.memberEmail);
    }
    return map;
  }, [projectData]);

  const allMembers = useMemo(() => [...engineers, ...qaMembers], [engineers, qaMembers]);
  const teamBlocks = useMemo(
    () => buildTeamBlocks(selectedOrg, allMembers, customViews, nameToEmail),
    [selectedOrg, allMembers, customViews, nameToEmail],
  );

  if (teamBlocks.length === 0) return null;

  const cols = teamBlocks.length;
  const gridClass =
    cols <= 1
      ? 'max-w-md'
      : cols === 2
        ? 'grid grid-cols-2 gap-x-4 gap-y-6'
        : cols === 3
          ? 'grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-6'
          : 'grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-6';

  return (
    <Card className="animate-content-reveal">
      <CardHeader title={t('members.healthDiagnosis.title')} />
      <CardContent>
        <div className={gridClass}>
          {teamBlocks.map((block) => (
            <TeamHealthTable key={block.key} block={block} projectsByEmail={projectsByEmail} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
